import Decimal from 'decimal.js';
import type { SplitAPInvoiceLineItemPart } from '@erp/rpc';

export interface SplitPartDraft {
  key: string;
  quantity: string;
  totalPrice: string;
}

export interface SplitAssessment {
  /** Sum of the part amounts, or null while any amount is not a number. */
  partsTotal: string | null;
  /** Line total minus the parts total, or null while any amount is not a number. */
  remaining: string | null;
  /** A part has the opposite sign of the line, so parts could cancel out. */
  hasOppositeSignPart: boolean;
  canSubmit: boolean;
}

const DECIMAL_PATTERN = /^-?\d+(\.\d+)?$/;

/** The server accepts 2–20 parts. */
export const MAX_SPLIT_PARTS = 20;

function readDecimal(
  value: string | number | null | undefined,
): Decimal | null {
  const text = String(value ?? '').trim();
  return DECIMAL_PATTERN.test(text) ? new Decimal(text) : null;
}

/** Quantities are stored with 4 decimals, amounts with 2. */
function isStorableQuantity(quantity: Decimal | null): boolean {
  return quantity !== null && quantity.gt(0) && quantity.decimalPlaces() <= 4;
}

function isStorableAmount(total: Decimal | null): boolean {
  return total !== null && !total.isZero() && total.decimalPlaces() <= 2;
}

/**
 * Each part keeps the line's sign: a credit line splits into credits. A line
 * that nets to zero can only split into a charge and its credit.
 */
function hasLineSign(total: Decimal, lineTotal: Decimal): boolean {
  return lineTotal.isZero() || total.isNegative() === lineTotal.isNegative();
}

function newPart(quantity: string, totalPrice: string): SplitPartDraft {
  return { key: crypto.randomUUID(), quantity, totalPrice };
}

function lineTotalOf(lineTotal: string | null | undefined): Decimal {
  return (readDecimal(lineTotal) ?? new Decimal(0)).toDecimalPlaces(2);
}

/**
 * Two parts that already add up to the line: the amount is halved (the
 * second part takes the odd cent) and each keeps the line's quantity.
 */
export function initialSplitParts(line: {
  quantity?: string | number | null;
  totalPrice?: string | null;
}): SplitPartDraft[] {
  const quantity = readDecimal(line.quantity)?.toString() ?? '1';
  const total = lineTotalOf(line.totalPrice);
  const half = total.dividedBy(2).toDecimalPlaces(2, Decimal.ROUND_DOWN);
  return [
    newPart(quantity, half.toFixed(2)),
    newPart(quantity, total.minus(half).toFixed(2)),
  ];
}

export function assessSplit(
  lineTotal: string | null | undefined,
  parts: readonly SplitPartDraft[],
): SplitAssessment {
  const totals = parts.map((part) => readDecimal(part.totalPrice));
  const quantities = parts.map((part) => readDecimal(part.quantity));
  if (totals.some((total) => total === null)) {
    return {
      partsTotal: null,
      remaining: null,
      hasOppositeSignPart: false,
      canSubmit: false,
    };
  }

  const total = lineTotalOf(lineTotal);
  const hasOppositeSignPart = totals.some(
    (part) => part !== null && !part.isZero() && !hasLineSign(part, total),
  );

  const partsTotal = totals.reduce<Decimal>(
    (sum, total) => sum.plus(total ?? 0),
    new Decimal(0),
  );
  const remaining = total.minus(partsTotal);
  const partsValid =
    parts.length >= 2 &&
    parts.length <= MAX_SPLIT_PARTS &&
    totals.every(isStorableAmount) &&
    quantities.every(isStorableQuantity) &&
    !hasOppositeSignPart;

  return {
    partsTotal: partsTotal.toFixed(2),
    remaining: remaining.toFixed(2),
    hasOppositeSignPart,
    canSubmit: partsValid && remaining.isZero(),
  };
}

export function canAddSplitPart(parts: readonly SplitPartDraft[]): boolean {
  return parts.length < MAX_SPLIT_PARTS;
}

/**
 * Add a part carrying whatever amount is still unassigned. When nothing is
 * left, or the parts already exceed the line, the new part starts empty:
 * pre-filling the overshoot as a negative part is how a $255 line became
 * 255 + 365 − 365 (AP-27).
 */
export function appendSplitPart(
  lineTotal: string | null | undefined,
  parts: readonly SplitPartDraft[],
  quantity: string,
): SplitPartDraft[] {
  const { remaining } = assessSplit(lineTotal, parts);
  const left = readDecimal(remaining);
  const prefill =
    left !== null && !left.isZero() && hasLineSign(left, lineTotalOf(lineTotal))
      ? left.toFixed(2)
      : '';
  return [...parts, newPart(quantity, prefill)];
}

export function unitPricePreview(part: SplitPartDraft): string | null {
  const total = readDecimal(part.totalPrice);
  const quantity = readDecimal(part.quantity);
  if (!total || !quantity || quantity.lte(0)) return null;
  return total.dividedBy(quantity).toDecimalPlaces(4).toString();
}

export function toSplitPayload(
  parts: readonly SplitPartDraft[],
): SplitAPInvoiceLineItemPart[] {
  return parts.map((part) => ({
    quantity: part.quantity.trim(),
    totalPrice: part.totalPrice.trim(),
  }));
}
